import React from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  ChevronRightIcon,
  ChevronLeftIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  CreditCardIcon,
  UserIcon,
} from 'lucide-react';
import { useDialogStore } from '../../store/dialogStore';
import { useTeamRegistrationStore } from '../../store/teamRegistrationStore';
import { TeamRegistrationService } from './TeamRegistrationService';
import TotalPrice from './components/TotalPrice';
import TaxSummary from './components/TaxSummary';
import MissingFieldsNotice from './components/MissingFieldsNotice';
import DialogBody from '../../components/customLayout/dialogBody';
import axiosInstance from '../../axiosConfig';
import { useAssociationStore } from '../../store/associationStore';
import { useGlobalStore } from '../../store/globalStore';

interface PreviewTabProps {
  onNext?: () => void;
  onPrevious?: () => void;
  showNextButton?: boolean;
  showPreviousButton?: boolean;
}

// Teams are derived from store in component

const PreviewTab: React.FC<PreviewTabProps> = ({
  onNext,
  onPrevious,
  showNextButton = true,
  showPreviousButton = true,
}) => {
  const openDialog = useDialogStore((state) => state.openDialog);
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const teams = useTeamRegistrationStore((state) => state.teams);
  const iban = useTeamRegistrationStore((state) => state.iban);
  const reporterName = useTeamRegistrationStore((state) => state.reporterName);
  const getReport = useTeamRegistrationStore((state) => state.getReport);
  const association = useAssociationStore((state) => state.association);
  const setFinalData = useTeamRegistrationStore((state) => state.setFinalData);
  const setLoading = useGlobalStore((state) => state.setLoading);
  const navigate = useNavigate();

  useEffect(() => {
    if (!teams || teams.length === 0) {
      navigate('/team-registration');
    }
  }, [teams, navigate]);

  const selectedTeams = teams
    .filter((t) => t.selectedTeams !== null)
    .map((t) => ({
      id: t.id,
      name: t.text,
      count: t.selectedTeams ?? 0,
      pricePerTeam: t.price,
      total: (t.selectedTeams ?? 0) * t.price,
      spielgemeinschaftClub:
        TeamRegistrationService.needsSpielgemeinschaftClubInput(
          association,
          t,
          t.selectedTeams,
        )
          ? (t.spielgemeinschaftClub || '').trim()
          : '',
    }));

  const missingSpielgemeinschaftClubs =
    TeamRegistrationService.missingSpielgemeinschaftClubs(association, teams);

  const openWarning = (content: React.ReactNode) =>
    openDialog(content, () => closeDialog(), true);

  const handleSubmit = () => {
    axiosInstance
      .post('/report/create', getReport())
      .then((response) => {
        setFinalData(response.data);
        closeDialog();
        setLoading(false);
        onNext?.();
      })
      .catch((error) => {
        setLoading(false);
        const message = error?.response?.data?.message;
        openWarning(
          <DialogBody className="py-2 text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <AlertTriangleIcon className="w-7 h-7 text-red-600" />
            </div>
            <div className="text-lg font-semibold text-gray-900">
              Die Meldung wurde nicht gesendet
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Ihre Angaben sind noch nicht abgeschickt.
            </p>
            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-left text-xs leading-relaxed text-gray-700">
              {message ||
                'Beim Senden der Mannschaftsmeldung ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.'}
            </div>
          </DialogBody>,
        );
      });
  };

  const handleNextStep = () => {
    if (onNext) {
      // Reachable without passing the Team tab (direct link, browser history),
      // so the mandatory club names are checked again right before sending.
      if (missingSpielgemeinschaftClubs.length > 0) {
        openWarning(
          <MissingFieldsNotice
            missingSpielgemeinschaftClubs={missingSpielgemeinschaftClubs}
            footnote={
              <>
                Sie finden das Feld im Schritt{' '}
                <span className="font-semibold text-[#52A445]">Team</span>.
              </>
            }
          />,
        );
        return;
      }

      openDialog(
        <div className="py-5">
          <div className="text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangleIcon className="w-8 h-8 text-amber-600" />
            </div>
            <div className="text-lg font-semibold text-gray-900 mb-2">
              Sind Sie sicher?
            </div>
            <div className="text-sm text-gray-600 mb-4">
              Möchten Sie die Mannschaftsliste wirklich an den{' '}
              <span className="font-semibold text-[#52A445]">
                {association?.name}
              </span>{' '}
              senden?
            </div>
          </div>
        </div>,
        () => {
          setLoading(true);
          handleSubmit();
        },
      );
    }
  };

  const handlePreviousStep = () => {
    if (onPrevious) {
      onPrevious();
    }
  };

  const totalAmount = selectedTeams.reduce((sum, team) => sum + team.total, 0);
  const totalTeamsCount = selectedTeams.reduce(
    (sum, team) => sum + team.count,
    0,
  );
  const {
    net: totalNet,
    vat: totalVat,
    gross: totalGross,
  } = TeamRegistrationService.computeTotalsFromTeams(teams);

  return (
    <div>
      {/* Teams Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">
            Gemeldete Mannschaften
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mannschaft
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Anzahl
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preis pro Team
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gesamt
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {selectedTeams.map((team) => (
                <tr
                  key={team.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <CheckCircleIcon className="h-5 w-5 text-[#52A445]" />
                      <div>
                        <div className="text-sm text-gray-900 font-bold">
                          {team.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          ID: {team.id}
                        </div>
                        {team.spielgemeinschaftClub && (
                          <div className="text-xs text-gray-600 mt-0.5">
                            Spielgemeinschaft mit:{' '}
                            <span className="font-medium text-gray-900">
                              {team.spielgemeinschaftClub}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold">
                      {team.count}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                    {TeamRegistrationService.formatCurrencyDE(
                      team.pricePerTeam,
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold text-gray-900">
                    {TeamRegistrationService.formatCurrencyDE(team.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tax Summary (same appearance as TeamTab) */}

      <TaxSummary
        net={totalNet}
        vat={totalVat}
        gross={totalGross}
        symbol={association?.symbol || ''}
        shouldShow={association?.symbol === 'LGV Hamburg' && totalVat > 0}
      />

      {/* Summary Section */}
      <div className="bg-gradient-to-r mt-5 from-blue-500/10 to-blue-500/5 rounded-lg border border-blue-500/20 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-bold text-lg">€</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Zusammenfassung
              </h3>
              <p className="text-sm text-gray-600">
                Gesamtbetrag Ihrer Mannschaftsmeldungen
              </p>
            </div>
          </div>
          <div className="text-right">
            <TotalPrice
              amount={totalAmount}
              className="text-2xl font-bold text-blue-600"
            />
            <div className="text-sm text-gray-500">
              {totalTeamsCount} Mannschaft{totalTeamsCount !== 1 ? 'en' : ''}{' '}
              gemeldet
            </div>
          </div>
        </div>
      </div>

      {/* Bank and Reporter Info */}
      <div className="grid grid-cols-1 md:grid-cols-10 gap-4 mb-6">
        {/* Reporter (30%) */}
        <div className="md:col-span-3 bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <UserIcon className="w-5 h-5 text-gray-700" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900">
                Meldung durch
              </h4>
              <p className="text-xs text-gray-500">Vor- und Nachname</p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-md border border-gray-200 p-3">
            <div className="text-sm font-medium text-gray-900 break-words">
              {reporterName || '—'}
            </div>
          </div>
        </div>

        {/* IBAN (70%) */}
        <div className="md:col-span-7 rounded-lg border border-gray-200 p-5">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 bg-[#52A445]/20 rounded-lg flex items-center justify-center">
              <CreditCardIcon className="w-5 h-5 text-[#52A445]" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900">
                SEPA-Zahlungsdaten
              </h4>
              <p className="text-xs text-gray-500">IBAN für den Einzug</p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div className="bg-gray-50 rounded-md border border-gray-200 p-3">
              <div className="text-xs text-gray-500 mb-1">IBAN</div>
              <div className="text-sm font-mono tracking-wide text-gray-900 break-all">
                {iban || '—'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {association?.symbol === 'BWGV' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-yellow-100 rounded-full flex items-center justify-center mt-0.5">
              <span className="text-yellow-600 text-xs font-bold">!</span>
            </div>
            <div>
              <h4 className="text-sm font-medium text-yellow-800">
                Wichtiger Hinweis
              </h4>
              <p className="text-sm text-yellow-700 mt-1">
                Mit Abgabe der Meldung verpflichtet sich der{' '}
                <span className="font-bold text-yellow-800">meldende Club</span>{' '}
                zur Ausrichtung{' '}
                <span className="font-bold text-yellow-800">
                  mind. eines BWGV-Verbandturniers
                </span>{' '}
                in der{' '}
                <span className="font-bold text-yellow-800">
                  gleichen Saison
                </span>{' '}
                (vgl.{' '}
                <span className="font-bold text-yellow-800">
                  BWGV-Turnierbedingungen
                </span>{' '}
                und{' '}
                <span className="font-bold text-yellow-800">Ligastatut</span>).
              </p>
            </div>
          </div>
        </div>
      )}

      {(showNextButton || showPreviousButton) && (
        <div className="mt-8 flex justify-between">
          {showPreviousButton && (
            <Button
              onClick={handlePreviousStep}
              className="px-6 flex items-center gap-2"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Zurück
            </Button>
          )}
          <div className="flex-1"></div>
          {showNextButton && (
            <Button
              onClick={handleNextStep}
              className="px-6 flex items-center gap-2 !bg-[#52A445] hover:!bg-[#52A445]/90"
            >
              <CheckCircleIcon className="h-4 w-4" />
              Bestätigen & Fortfahren
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default PreviewTab;
